export enum FeatureFlag {
  CHECKOUT_DISABLE_SUCCESS_SCREEN = 'checkout_disable_success_screen',
  CHECKOUT_DEEPLINKS_DISABLE_STORE = 'checkout_deeplinks_disable_store',
  CHECKOUT_OPEN_CLOSE_ANIMATION = 'checkout_open_close_animation',
  CHECKOUT_PARTIAL_SCREEN_MOBILE = 'checkout_partial_screen_mobile',
  CHECKOUT_DISABLE_PAYMENT_LINKS_REDIRECT_SUCCESS_BUTTON = 'checkout_disable_payment_links_redirect_success_button',
  CHECKOUT_DISPLAY_LINK_EXPRESS_AS_APM_BOX = 'checkout_display_link_express_as_apm_box',
  CHECKOUT_DISABLE_PROCESSING_SCREEN = 'checkout_disable_processing_screen',
  STRIPE_SAVED_CARD_NEW_FLOW = 'checkout_use_saved_card',
  CHECKOUT_ENFORCE_CUSTOMER_EMAIL = 'checkout_enforce_customer_email',
  CHECKOUT_DISPLAY_PAYPAL_AS_APM_BOX = 'checkout_display_paypal_as_apm_box',
  CHECKOUT_REMOVE_FIELDS_LABELS = 'checkout_remove_field_labels',
  /**
   * @deprecated Declared but never read in v3 — superseded by
   * {@link FeatureFlag.CHECKOUT_EXPANDED_DETAILS}. Setting it has no effect;
   * publishers who relied on `false` to get an expanded product list in v2
   * need the new flag ON instead (ACDEV-25284). Kept in the enum so the BE
   * payload still type-checks until the flag is retired there.
   */
  CHECKOUT_MOBILE_COLLAPSED_BREAKDOWN = 'checkout_mobile_collapsed_breakdown',
  CHECKOUT_PAYMENT_METHODS_DIVIDER = 'checkout_payment_methods_divider',
  CHECKOUT_SELECT_SAVE_CARD_BY_DEFAULT = 'checkout_select_save_card_by_default',
  CHECKOUT_SHOW_BACK_TO_GAME_BUTTON = 'checkout_show_back_to_game_button',
  CHECKOUT_SHOW_ADYEN_CARDHOLDER_NAME_FIELD = 'checkout_show_adyen_cardholder_name_field',
  /**
   * Gate raw-PAN (self-managed Appcharge Payment Element) card entry. The
   * cardholder-name field is driven solely by
   * `checkout_show_adyen_cardholder_name_field` and is independent of this flag.
   */
  CHECKOUT_ALLOW_RAW_PAN_PAYMENTS = 'checkout_allow_raw_pan_payments',
  CHECKOUT_SHOW_PAYMENT_METHOD_ICONS = 'checkout_show_payment_method_icons',
  CHECKOUT_SHOW_LANDSCAPE_ROTATE = 'checkout_show_landscape_rotate',
  /**
   * Fold the always-visible "Total" row out of the Details header and into the
   * collapsible breakdown, so a COLLAPSED header shows only the offer name +
   * "Details" toggle (the total is revealed when the breakdown is expanded).
   * Applies to every template. See `Details.tsx` / `shouldCollapseTotal`.
   */
  CHECKOUT_COLLAPSE_TOTAL = 'checkout_collapse_total',
  /**
   * ACDEV-25284 — start the Details section EXPANDED, so the product list,
   * coupon field and subtotal/tax are visible without tapping the "Details"
   * toggle.
   *
   * Replaces `CHECKOUT_MOBILE_COLLAPSED_BREAKDOWN`, which publishers used in
   * checkout v2 (as `false`) to get an expanded product list but which v3
   * never wired up — v3 reads the per-template `defaultDetailsOpen` constant
   * instead, and DEFAULT hardcodes it to collapsed. Deliberately expressed as
   * a positive opt-in rather than reviving the inverted flag, so OFF/absent
   * keeps every existing publisher byte-for-byte.
   *
   * When ON it beats the per-template `defaultDetailsOpen` and the collapse
   * exceptions for landscape / raw card forms / the PREFERRED returning-card
   * branch. It does NOT beat a host `defaultOpen` prop — the swipe-up sheet
   * passes `false` and stays compact regardless of this flag. The toggle
   * still lets the shopper collapse it afterwards.
   *
   * See `resolveDetailsDefaultOpen` in `Details.tsx` / `shouldExpandDetails`.
   */
  CHECKOUT_EXPANDED_DETAILS = 'checkout_expanded_details',
  /**
   * Sprint 87 — pulse the primary "Pay" CTA while the form is actionable
   * and idle, to reduce time-to-tap on the conversion-critical action.
   * Wallet buttons (Apple Pay, Google Pay, PayPal, Venmo) are explicitly
   * out of scope and untouched by this flag. See
   * `components/PaymentInputs/PayButton.tsx` for the gating logic.
   * Flag key kept camelCase to match the BE flag-service registration.
   */
  PAY_CTA_IDLE_PULSE = 'payCtaIdlePulse',
  /**
   * Gate Mixpanel Session Replay (rrweb) recording behind an explicit,
   * per-publisher opt-in. When OFF (default/absent), no session is ever
   * recorded regardless of environment or `VITE_SESSION_REPLAY_PERCENT`.
   * When ON, recording still additionally requires production + the sampling
   * percent. Event tracking (`mixpanel.track`) is unaffected by this flag —
   * only replay recording is gated. See `@/constants/sessionReplay`.
   */
  ENABLE_MIXPANEL_SESSION_RECORDING = 'enable_mixpanel_session_recording',
  /**
   * ACDEV-30261: same as `ENABLE_MIXPANEL_SESSION_RECORDING`, but gates Session
   * Replay on the subscription checkout (`/subscriptions/:token`) only, so it
   * can be opened per publisher without also recording their one-off checkout.
   */
  ENABLE_SUBSCRIPTIONS_SESSION_RECORDING = 'enable_subscriptions_session_recording',
  /**
   * Present the FTD / last-payment (PREFERRED_PAYMENT_METHOD) checkout as an
   * Apple/Google-Pay-style bottom-sheet drawer on mobile portrait instead of
   * the full-screen modal — but ONLY when the primary method is a native
   * wallet (Apple Pay / Google Pay). Every other case (non-native last
   * method, landscape, desktop, flag OFF/absent) keeps the existing modal
   * flow byte-for-byte, so rollout is per-publisher with instant rollback.
   *
   * Off/absent by default. Flag key kept camelCase to match the BE
   * flag-service registration (`checkoutDrawerForLastMethod`). See
   * `CheckoutBottomSheet` / `PreferredPaymentView`.
   */
  CHECKOUT_DRAWER_FOR_LAST_METHOD = 'checkoutDrawerForLastMethod',
  /**
   * ACDEV-25067: gate the wallet-derived ZIP + tax flow. When ON, US/CA
   * customers on Apple/Google Pay (Stripe ECE + Adyen) get the ZIP from the
   * wallet sheet and we recompute tax in-sheet + forward the ZIP at finalize.
   * When OFF, the pre-branch behavior is restored: on-page `InputTaxComponent`
   * captures ZIP, wallet sheets are ZIP-agnostic, and the express-buttons
   * ZIP-blocker overlay is re-enabled.
   */
  WALLET_ZIP_TAX = 'checkout_wallet_zip_tax',
  /**
   * Soft kill switch for Nuvei Apple Pay / Google Pay Web SDK wallets.
   * Missing/undefined keeps wallets enabled when routing selects Nuvei;
   * explicit `false` hides them (ops rollback without re-routing).
   */
  NUVEI_WALLETS = 'nuvei_wallets',
  /**
   * ACDEV-26742: show the publisher company name as the Adyen Apple Pay
   * sheet's grand-total row label ("PAY ACME GAMES", per Apple's
   * payment-sheet guidance) — display-only (PassKit `total.label` via Adyen
   * `totalPriceLabel`), never sent to the PSP/bank.
   * OFF/absent keeps the pre-flag behavior byte-for-byte: no FE
   * `totalPriceLabel` (SDK falls back to `configuration.merchantName`) and
   * the plain 'Total' label in the wallet ZIP-tax breakdown path.
   * Flag key spelled `apple_play…` to match the BE flag-service registration.
   */
  APPLE_PAY_COMPANY_NAME_LABEL = 'apple_play_company_name_label',
  /**
   * Short-term OR_BIBED_15 mitigation: render Stripe-routed Google Pay as a
   * DIRECT pay.js integration — our page calls `loadPaymentData` and the
   * token tokenizes via `gateway: 'stripe'` — instead of Stripe's Express
   * Checkout Element. ECE's WebKit relay (returnUrl through b.stripecdn.com)
   * fails at the approval step on iOS; the direct flow keeps the
   * `window.opener` handback, the architecture Adyen/Nuvei Google Pay
   * already use successfully in prod. OFF/absent keeps ECE byte-for-byte.
   * Scope: the Google Pay tile / APM form (`GooglePayForm`) on REAL
   * iOS/iPadOS devices only (`isIOSUserAgent`) — the broken WebKit relay is
   * iOS-specific, so flag-ON still keeps ECE on Android/desktop. The combined
   * express row (`PayButtonByDevice`) is untouched.
   */
  STRIPE_DIRECT_GOOGLE_PAY = 'checkout_stripe_direct_google_pay',
  /**
   * Soft kill switch for the Apple Pay new-tab fallback. Apple Pay in a
   * cross-origin iframe needs either Safari 17+ `allow="payment"` AND a
   * parent domain registered with the PSP, or a top-level tab on our own
   * verified domain. We render a fake Apple Pay button that opens that tab
   * only where the real one is known-dead: iOS <= 16 (no session in frame),
   * or after the PSP reports Apple Pay unavailable / Apple rejects the
   * merchant session. Framed iOS 17+ gets the real button first.
   * Missing/undefined keeps the fallback enabled; explicit `false`
   * disables it (ops rollback). See `utils/applePayTabFallback.ts`.
   */
  APPLE_PAY_TAB_FALLBACK = 'apple_pay_tab_fallback',
  /**
   * ACDEV-26410: recover an Adyen 3DS challenge whose page was discarded
   * mid-authentication. Spanish SCA is app-based — CaixaBank and Santander Key
   * push a notification the shopper confirms in the banking app — so the tab
   * is backgrounded for the whole challenge and Chrome discards it under
   * memory pressure. Production shows 36 `pagehide` events during an Adyen
   * challenge in Spain against 1 in France, and 27% of Spanish challenges are
   * backgrounded and never return (4% in France), converting at 20% against
   * 52% for those that stay.
   *
   * The existing order-state watchdog already covers the survivable case (a
   * freeze resumes with the JS context intact); it cannot cover a discard,
   * because it dies with the page. When ON, the pending challenge is mirrored
   * into `sessionStorage` so the reloaded checkout can settle from the order
   * webhook or re-mount the action. OFF/absent keeps the current behavior
   * byte-for-byte: nothing is written and nothing is resumed.
   * See `utils/pendingChallengeStore.ts`.
   */
  ADYEN_3DS_CHALLENGE_RESUME = 'checkout_adyen_3ds_challenge_resume',
  /**
   * Force the shopper-entered promo code to uppercase — both in the visible
   * field and in everything that leaves the client (the `apply-promo-code`
   * request, the stored `promoCodeName`, and the analytics / SDK / redirect
   * payloads derived from it). For publishers whose codes are issued
   * uppercase and whose BE matches case-sensitively, so `save10` no longer
   * fails against a `SAVE10` coupon.
   * OFF/absent keeps the pre-flag behavior byte-for-byte: the raw keystrokes
   * are stored and sent as typed. See `utils/promoCode.normalizePromoCode`
   * and `components/PromoCode/PromoCode.tsx`.
   */
  CHECKOUT_PROMO_FORCE_UPPER_CASE = 'checkout_promo_force_upper_case',
  /**
   * ACDEV-29368 — publisher opt-in kill switch for browser pull-to-refresh
   * when the checkout owns the top-level browsing context (redirect mode /
   * Payment Links / direct-tab load).
   *
   * PTR at the top-level tab is destructive for a payment flow: a completed
   * pull refreshes mid-payment, and on iOS 17 Safari an aborted mid-pull
   * wedges the root scroll arbiter and freezes the inner scrollport
   * (reproduced against SB + STG).
   *
   * When ON, `useDisablePullToRefresh` toggles the
   * `checkout-disable-pull-to-refresh` class on `<html>` and the global CSS
   * scoped to that class locks the root element to `height: 100dvh;
   * overflow: hidden`. Per the CSS Overflow spec, the root's `overflow`
   * value propagates to the viewport, so the viewport itself becomes
   * non-scrollable and PTR cannot arm. This is the only reliable way to
   * disable PTR across WebKit and Blink — `overscroll-behavior: contain`
   * is not honored by WebKit for the PTR gesture. Inner scroll containers
   * inside `Modal` are unaffected because `Modal` is `fixed inset-0` and
   * CSS overflow does not cascade to descendants.
   *
   * OFF/absent keeps PTR enabled byte-for-byte. No-op inside an iframe
   * (SDK / overlay embed) — PTR belongs to the merchant page there.
   */
  CHECKOUT_DISABLE_PULL_TO_REFRESH = 'checkout_disable_pull_to_refresh',
  /**
   * ACDEV-30609 — mount the publisher's accessibility widget (EqualWeb) on the
   * checkout, with a draggable icon the shopper can park anywhere.
   *
   * The script is the per-publisher artifact already served from
   * `media.appcharge.com/accessibility/<publisherId>/`, the same one the
   * webstore loads behind `Store_accessibility_script_support` — so this flag
   * only decides whether the checkout mounts it too, not whether the publisher
   * has a widget at all. Turning it on for a publisher with nothing uploaded
   * is inert (empty script, no icon).
   */
  STORE_ACCESSIBILITY_SCRIPT_SUPPORT = 'Store_accessibility_script_support',
  /**
   * ACDEV-30386 — publisher opt-in to drop the success-screen CTA
   * (`back-to-store-button`, labelled "Back to Store" for overlay sessions and
   * "Back to Game" once a redirect attempt has elapsed). v2 overlay success
   * showed no CTA at all; v3 always mounts one, which SciPlay asked to remove.
   *
   * Button-only: the rest of the success screen (confetti, title, order id),
   * the top-bar X and Payment Links auto-redirect are untouched. Distinct from
   * `CHECKOUT_SHOW_BACK_TO_GAME_BUTTON` (CTA *instead of* auto-redirect) and
   * `CHECKOUT_DISABLE_SUCCESS_SCREEN` (no success screen at all).
   */
  CHECKOUT_HIDE_BACK_TO_GAME_BUTTON = 'checkout_hide_back_to_game_button',
}
